import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, Lock, Shield, ArrowRight, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getDomainFavicon = (url) => {
  if (!url) return null;
  try {
    const domain = new URL(url.startsWith('http') ? url : `https://${url}`).hostname;
    return `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
  } catch { return null; }
};

export default function SentPage({ user }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchSent = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/sent`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(res.data);
    } catch (err) { toast.error('Failed to load sent items'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSent(); }, [fetchSent]);

  const handleClick = (item) => {
    if (item.type === 'credential') navigate(`/dashboard/credential/${item.id}`);
    else navigate(`/dashboard/certificates/${item.id}`);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="text-text-primary font-manrope text-xl">Loading sent items...</div></div>;
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Send className="w-8 h-8 text-primary" />
          <h1 className="font-manrope font-bold text-4xl text-text-primary tracking-tight">Sent</h1>
        </div>
        <p className="text-text-secondary">Items you've shared with your team</p>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-20">
          <Send className="w-16 h-16 text-text-muted mx-auto mb-4" strokeWidth={1.5} />
          <h3 className="font-manrope font-semibold text-xl text-text-primary mb-2">Nothing shared yet</h3>
          <p className="text-text-secondary">You haven't shared any credentials or certificates</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((item) => {
            const favicon = getDomainFavicon(item.url);
            return (
              <div
                key={`${item.type}-${item.id}`}
                onClick={() => handleClick(item)}
                className="bg-white border-2 border-gray-200 rounded-xl p-5 flex items-center gap-4 cursor-pointer hover:border-primary/50 hover:shadow-md transition-all group"
                data-testid={`sent-item-${item.id}`}
              >
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {favicon ? (
                    <img src={favicon} alt="" className="w-5 h-5" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }} />
                  ) : null}
                  <span style={{ display: favicon ? 'none' : 'block' }}>
                    {item.type === 'credential' ? <Lock className="w-5 h-5 text-primary" /> : <Shield className="w-5 h-5 text-primary" />}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-text-primary truncate">{item.title}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${item.type === 'credential' ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600'}`}>
                      {item.type}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <Users size={13} className="text-text-muted" />
                    <p className="text-text-muted text-sm">
                      Shared with{' '}
                      {item.shared_with_users.slice(0, 3).map((u, i) => (
                        <span key={u.id}>
                          {i > 0 && ', '}
                          <span className="font-medium text-text-secondary">{u.name}</span>
                        </span>
                      ))}
                      {item.shared_count > 3 && <span className="text-text-muted"> +{item.shared_count - 3} more</span>}
                    </p>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xs text-text-muted">{new Date(item.updated_at).toLocaleDateString()}</p>
                  <p className="text-xs text-primary font-medium mt-1">{item.shared_count} recipient{item.shared_count !== 1 ? 's' : ''}</p>
                </div>
                <ArrowRight size={16} className="text-text-muted group-hover:text-primary transition-colors flex-shrink-0" />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
